import { QueryClient } from '@tanstack/react-query';

// Optimized QueryClient configuration
export const createOptimizedQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Stale time based on data volatility
        staleTime: 5 * 60 * 1000, // 5 minutes for most data
        gcTime: 10 * 60 * 1000, // 10 minutes cache (formerly cacheTime)
        retry: (failureCount, error: any) => {
          // Don't retry on auth errors
          if (error?.status === 401 || error?.status === 403) {
            return false;
          }
          // Retry up to 3 times for other errors
          return failureCount < 3;
        },
        retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
        // Network mode to handle offline scenarios
        networkMode: 'online',
        // Refetch on window focus for critical data only
        refetchOnWindowFocus: false,
        // Enable background updates
        refetchOnReconnect: 'always',
      },
      mutations: {
        retry: (failureCount, error: any) => {
          // Never retry mutations on client errors (4xx)
          if (error?.status >= 400 && error?.status < 500) {
            return false;
          }
          // Retry server errors (5xx) once
          return failureCount < 1;
        },
        // Network mode for mutations
        networkMode: 'online',
      },
    },
  });
};

// Configure different stale times for different data types
export const queryKeys = {
  // Real-time data - short stale time
  orders: {
    staleTime: 30 * 1000, // 30 seconds
  },
  // User profiles - medium stale time
  profiles: {
    staleTime: 2 * 60 * 1000, // 2 minutes
  },
  // Products - longer stale time
  products: {
    staleTime: 5 * 60 * 1000, // 5 minutes
  },
  // Static content - very long stale time
  documents: {
    staleTime: 30 * 60 * 1000, // 30 minutes
  },
  training: {
    staleTime: 15 * 60 * 1000, // 15 minutes
  },
} as const;

// Query configuration presets for different priorities
export const queryConfigs = {
  highPriority: {
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000, // 5 minutes cache (formerly cacheTime)
    refetchOnWindowFocus: true,
  },
  normal: {
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000, // 10 minutes cache (formerly cacheTime)
    refetchOnWindowFocus: false,
  },
  lowPriority: {
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000, // 60 minutes cache (formerly cacheTime)
    refetchOnWindowFocus: false,
  },
} as const;